import {useEffect, useRef, useState} from 'react';
import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Box,
  Button,
  Text,
  VStack,
  useDisclosure,
} from '@chakra-ui/react';
import {useUser} from '../../contexts/UserContext';
import {
  patientDialect,
  patientDisplayName,
  patientLanguage,
} from './createSessionFormUtils';
import {
  CREATE_SESSION_CARD_PADDING,
  CreateSessionFooter,
  InterpreterSection,
  LocationSection,
  PatientSection,
} from './CreateSessionFormSections';
import CreateSessionScheduleModal from './CreateSessionScheduleModal';
import usePlannerStyles from './usePlannerStyles';
import useCreateSessionPatients from './useCreateSessionPatients';
import useEncounterLauncher from './useEncounterLauncher';

export default function CreateSessionForm({
  initialPatientItems = [] as any,
  onScheduled = null as any,
  fromPlanner = false,
} = {}) {
  const {userData} = useUser();
  const [patientSearch, setPatientSearch] = useState('');
  const [isPatientTypeaheadOpen, setIsPatientTypeaheadOpen] = useState(false);
  const [selectedPatientItem, setSelectedPatientItem] = useState<any>(null);
  const [shouldFocusPatientSearch, setShouldFocusPatientSearch] =
    useState(false);
  const patientTypeaheadRef = useRef<any>(null);
  const noPatientConfirm = useDisclosure();
  const cancelNoPatientRef = useRef<any>(null);
  const autoSeededLanguagePatientRef = useRef<any>(null);

  const {
    filteredPatients,
    hasActiveEhr,
    isLoadingPatients,
    isSearchingEhrPatients,
  } = useCreateSessionPatients({userData, patientSearch, initialPatientItems});

  const {
    alreadyWithPatient,
    setAlreadyWithPatient,
    phoneJoinMode,
    setPhoneJoinMode,
    callerId,
    setCallerId,
    createSessionDraft,
    createSessionValidation,
    canShowSchedule,
    dialect,
    dialectOptions,
    dialectSearch,
    dialectTypeaheadRef,
    filteredDialectOptions,
    filteredLanguageOptions,
    handleClearDialect,
    handleClearLanguage,
    handleDialectSearchChange,
    handleInterpreterToggle,
    handleLocationChange,
    handleLanguageFocus,
    handleLanguageSearchChange,
    handleSelectDialectOption,
    handleSelectLanguageOption,
    handleStart,
    interpreterEnabled,
    simultaneousEnabled,
    setSimultaneousEnabled,
    isDialectTypeaheadOpen,
    isLanguageTypeaheadOpen,
    isStarting,
    language,
    languageSearch,
    languageTypeaheadRef,
    locationType,
    openScheduleModal,
    patientPhone,
    providerPhone,
    scheduleModal,
    seedLanguageSelection,
    setIsDialectTypeaheadOpen,
    setIsLanguageTypeaheadOpen,
    setPatientPhone,
    setProviderPhone,
    setVideoCallLink,
    shouldShowDialectTypeahead,
    shouldShowLanguageSearchStatus,
    visibleErrors,
    videoCallLink,
  } = useEncounterLauncher({
    patientItem: selectedPatientItem,
    // The patient is always optional now — no selection is treated as a
    // deliberate skip. EHR-linked orgs still confirm before starting without
    // one so a missing chart link is intentional.
    patientChoiceRequired: false,
    fromPlanner,
    onScheduled,
  });

  const {surfaceBg, borderColor, cardShadow, textColor} = usePlannerStyles();

  // Simultaneous interpretation is only offered for the web phone call-out
  // (provider on web + patient dialed in). In-person and "already with the
  // patient" have no separate phone leg to translate against.
  //
  // DORMANT: the toggle is hidden for now while the gpt-realtime-translate
  // path is still being validated. All the plumbing (launcher state, backend
  // workflow flag, agent bridge) stays intact behind it; flip this to `true`
  // to surface the toggle again.
  const SHOW_SIMULTANEOUS_TOGGLE = false;
  const isWebDialOut =
    locationType === 'phone' && !alreadyWithPatient && phoneJoinMode === 'web';

  useEffect(() => {
    const handlePointerDown = (event: any) => {
      const target = event.target;
      const patientDropdown = document.getElementById(
        'create-session-patient-results',
      );
      if (
        patientTypeaheadRef.current &&
        !patientTypeaheadRef.current.contains(target) &&
        !patientDropdown?.contains(target)
      ) {
        setIsPatientTypeaheadOpen(false);
      }
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);

    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, []);

  // Auto-fill the interpreter language/dialect from the selected patient. This
  // fires for both orderings — picking the patient first then turning on the
  // interpreter, or enabling the interpreter then picking a patient — so the
  // provider doesn't retype a language the chart already knows. Seeded once per
  // patient (tracked by ref) so it never clobbers a language the provider then
  // clears or changes by hand.
  useEffect(() => {
    const patientKey = selectedPatientItem?.id || null;
    if (!patientKey) {
      autoSeededLanguagePatientRef.current = null;
      return;
    }
    if (!interpreterEnabled) return;
    if (autoSeededLanguagePatientRef.current === patientKey) return;
    const seededLanguage = patientLanguage(selectedPatientItem);
    if (!seededLanguage) return;
    autoSeededLanguagePatientRef.current = patientKey;
    seedLanguageSelection(seededLanguage, patientDialect(selectedPatientItem));
  }, [interpreterEnabled, selectedPatientItem]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedPatientName = patientDisplayName(selectedPatientItem);
  const shouldShowResults = isPatientTypeaheadOpen && !selectedPatientItem;

  const handleSelectPatient = (item: any) => {
    setSelectedPatientItem(item);
    setPatientSearch('');
    setIsPatientTypeaheadOpen(false);
    setShouldFocusPatientSearch(false);
  };

  const handleClearPatient = () => {
    setSelectedPatientItem(null);
    setPatientSearch('');
    setIsPatientTypeaheadOpen(false);
    setShouldFocusPatientSearch(false);
  };

  // Clicking an already-selected patient swaps straight back to the search
  // field (focused, dropdown open) so changing the patient is a single click
  // instead of clear-then-reselect.
  const handleEditPatient = () => {
    setSelectedPatientItem(null);
    setPatientSearch('');
    setIsPatientTypeaheadOpen(true);
    setShouldFocusPatientSearch(true);
  };

  const handlePatientSearchChange = (event: any) => {
    setPatientSearch(event.target.value);
    setIsPatientTypeaheadOpen(true);
  };

  // EHR-linked orgs may start without a patient, but we confirm first so a
  // missing chart link is a deliberate choice rather than an accident.
  const needsNoPatientConfirm = hasActiveEhr && !selectedPatientItem;

  const handleStartClick = () => {
    if (needsNoPatientConfirm) {
      noPatientConfirm.onOpen();
      return;
    }
    handleStart();
  };

  const handleConfirmNoPatient = () => {
    noPatientConfirm.onClose();
    handleStart();
  };

  return (
    <Box
      bg={surfaceBg}
      borderWidth="1px"
      borderColor={borderColor}
      borderRadius="xl"
      boxShadow={cardShadow}
      overflow="visible"
      data-testid="create-session-form"
      data-session-location={createSessionDraft.location.type}
      data-interpreter-enabled={
        createSessionDraft.capability.interpreterEnabled ? 'true' : 'false'
      }
      data-session-valid={createSessionValidation.isValid ? 'true' : 'false'}
    >
      <VStack spacing={0} align="stretch">
        <VStack spacing={4} align="stretch" p={CREATE_SESSION_CARD_PADDING}>
          <Text
            as="h2"
            color={textColor}
            fontSize="2xl"
            fontWeight="semibold"
            lineHeight="1.25"
            letterSpacing="0"
            mb={0}
            pt={1}
            pb={2}
            noOfLines={2}
            wordBreak="break-word"
          >
            New Encounter
          </Text>
          <PatientSection
            autoFocusPatientSearch={shouldFocusPatientSearch}
            filteredPatients={filteredPatients}
            handleClearPatient={handleClearPatient}
            handleEditPatient={handleEditPatient}
            handlePatientSearchChange={handlePatientSearchChange}
            handleSelectPatient={handleSelectPatient}
            isLoadingPatients={isLoadingPatients}
            isSearchingEhrPatients={isSearchingEhrPatients}
            onPatientSearchFocus={() => setIsPatientTypeaheadOpen(true)}
            patientSearch={patientSearch}
            patientTypeaheadRef={patientTypeaheadRef}
            selectedPatientItem={selectedPatientItem}
            selectedPatientName={selectedPatientName}
            shouldShowResults={shouldShowResults}
            visibleErrors={visibleErrors}
          />

          <InterpreterSection
            dialect={dialect}
            dialectOptions={dialectOptions}
            dialectSearch={dialectSearch}
            dialectTypeaheadRef={dialectTypeaheadRef}
            filteredDialectOptions={filteredDialectOptions}
            filteredLanguageOptions={filteredLanguageOptions}
            handleClearDialect={handleClearDialect}
            handleClearLanguage={handleClearLanguage}
            handleDialectSearchChange={handleDialectSearchChange}
            handleInterpreterToggle={handleInterpreterToggle}
            handleLanguageFocus={handleLanguageFocus}
            handleLanguageSearchChange={handleLanguageSearchChange}
            handleSelectDialectOption={handleSelectDialectOption}
            handleSelectLanguageOption={handleSelectLanguageOption}
            interpreterEnabled={interpreterEnabled}
            simultaneousEnabled={simultaneousEnabled}
            setSimultaneousEnabled={setSimultaneousEnabled}
            showSimultaneous={SHOW_SIMULTANEOUS_TOGGLE && isWebDialOut}
            isDialectTypeaheadOpen={isDialectTypeaheadOpen}
            isLanguageTypeaheadOpen={isLanguageTypeaheadOpen}
            language={language}
            languageSearch={languageSearch}
            languageTypeaheadRef={languageTypeaheadRef}
            setIsDialectTypeaheadOpen={setIsDialectTypeaheadOpen}
            setIsLanguageTypeaheadOpen={setIsLanguageTypeaheadOpen}
            showTopDivider
            shouldShowDialectTypeahead={shouldShowDialectTypeahead}
            shouldShowLanguageSearchStatus={shouldShowLanguageSearchStatus}
            visibleErrors={visibleErrors}
          />

          <LocationSection
            alreadyWithPatient={alreadyWithPatient}
            handleLocationChange={handleLocationChange}
            interpreterEnabled={interpreterEnabled}
            locationType={locationType}
            patientPhone={patientPhone}
            phoneJoinMode={phoneJoinMode}
            callerId={callerId}
            setCallerId={setCallerId}
            providerPhone={providerPhone}
            setAlreadyWithPatient={setAlreadyWithPatient}
            setPatientPhone={setPatientPhone}
            setPhoneJoinMode={setPhoneJoinMode}
            setProviderPhone={setProviderPhone}
            setVideoCallLink={setVideoCallLink}
            visibleErrors={visibleErrors}
            videoCallLink={videoCallLink}
          />
        </VStack>

        <CreateSessionFooter
          canSchedule={canShowSchedule}
          canStart={createSessionValidation.isValid}
          isStarting={isStarting}
          onSchedule={openScheduleModal}
          onStart={handleStartClick}
        />
      </VStack>

      <CreateSessionScheduleModal
        dateOptions={scheduleModal.dateOptions}
        isOpen={scheduleModal.isOpen}
        isSubmitting={scheduleModal.isSubmitting}
        onClose={scheduleModal.onClose}
        onDateChange={scheduleModal.onDateChange}
        onSubmit={scheduleModal.onSubmit}
        onTimeChange={scheduleModal.onTimeChange}
        scheduledDateKey={scheduleModal.scheduledDateKey}
        scheduledTimeKey={scheduleModal.scheduledTimeKey}
        userTimezoneLabel={scheduleModal.userTimezoneLabel}
        visibleScheduleError={scheduleModal.visibleScheduleError}
      />

      <AlertDialog
        isOpen={noPatientConfirm.isOpen}
        leastDestructiveRef={cancelNoPatientRef}
        onClose={noPatientConfirm.onClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent borderRadius="xl" mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="semibold">
              No patient selected
            </AlertDialogHeader>
            <AlertDialogBody>
              You have not picked a patient. Are you sure you want to start
              without one?
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button
                ref={cancelNoPatientRef}
                variant="ghost"
                onClick={noPatientConfirm.onClose}
              >
                Go back
              </Button>
              <Button
                variant="opalitePrimary"
                ml={3}
                onClick={handleConfirmNoPatient}
              >
                Start without a patient
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
